import { Alert, Button, Input, Result, Typography } from 'antd'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { ThreeDot } from 'react-loading-indicators'
import { ExclamationCircleFilled, ReloadOutlined } from '@ant-design/icons'
import { productApi } from 'api/product'
import { useSocketRequest } from 'shared/hooks/useSocketRequest'
import { EEnhancementImageType, EnhancementImageResponse } from 'models/product'
import { OrangeButton, VariantButton } from 'components/ui'
import { IMAGES_URL } from 'shared/constants/urls'
import styles from '../AiEnhancementModal.module.scss'

interface IRemoveProductImageBackgroundProps {
  isVisible: boolean
  productId: string
  imagesWithBackground: EnhancementImageResponse[]
  setImagesWithBackground: (images: EnhancementImageResponse[]) => void
  selectedImageWithBackground: EnhancementImageResponse | null
  setSelectedImageWithBackground: (image: EnhancementImageResponse | null) => void
  onSave: () => void
}

export const Photo: React.FC<IRemoveProductImageBackgroundProps> = memo(({ productId, isVisible, imagesWithBackground, setImagesWithBackground, selectedImageWithBackground, setSelectedImageWithBackground, onSave }) => {
  const [image, setImage] = useState<string | null>(null)
  const [additionalPrompt, setAdditionalPrompt] = useState('')

  const [additionalPromptForBackground, setAdditionalPromptForBackground] = useState('')

  const createJob = useCallback(async () => {
    const { data } = await productApi.removeProductImageBackground({
      productId,
      additionalPrompt
    })
    return { jobId: data.jobId }
  }, [productId, additionalPrompt])

  const createJobForBackground = useCallback(async () => {
    const { data } = await productApi.createAiImage({
      productId,
      additionalPrompt: additionalPromptForBackground,
      type: EEnhancementImageType.BACKGROUND,
      removedBackgroundLink: image as string
    })
    return { jobId: data.jobId }
  }, [productId, additionalPromptForBackground, image])

  const { isLoaded, isError, data, retry } = useSocketRequest<string>({
    enabled: false,
    createJob
  })

  const { isLoaded: isLoadedForBackground, isError: isErrorForBackground, data: dataForBackground, retry: retryForBackground } = useSocketRequest<EnhancementImageResponse>({
    enabled: false,
    createJob: createJobForBackground
  })

  useEffect(() => {
    if (!image && isVisible) {
      retry()
    }
  }, [image, isVisible])

  useEffect(() => {
    if (data) {
      setImage(data)
    }
  }, [data])

  useEffect(() => {
    if (dataForBackground) {
      setImagesWithBackground([...imagesWithBackground, dataForBackground])
      setSelectedImageWithBackground(dataForBackground)
    }
  }, [dataForBackground])

  const isImagesWithBackground = useMemo(() => !!imagesWithBackground.length, [imagesWithBackground])

  return (
    <div style={{ display: isVisible ? 'block' : 'none' }}>
      <Typography.Text className={styles.stepText}>Шаг 1. Работа с объектом</Typography.Text>
      <Alert message='Убедитесь что весь фон был обрезан корректно.' type='warning' showIcon icon={<ExclamationCircleFilled style={{ color: '#FF7905' }} />} className={styles.alertWarning} />
      <div className={styles.imageWrapper}>
        {!isLoaded && <ThreeDot color='#FF7905' size='medium' text='' textColor='' />}
        {isError && isLoaded && (
          <Result
            status='error'
            title='Ошибка при генерации изображения'
            subTitle='Попробуйте еще раз'
            extra={[
              <OrangeButton onClick={retry} disabled={!isLoaded}>
                Попробовать еще раз
              </OrangeButton>
            ]}
          />
        )}
        {isLoaded && image && !isError && <img src={image} alt='product' className={styles.image} />}
      </div>
      {isLoaded && image && (
        <>
          <div className={styles.removeBackgroundAdditionalPromptWrapper}>
            <Input.TextArea autoSize={true} placeholder='Точно опишите объект на фото, если результат не удовлетворил' value={additionalPrompt} onChange={(e) => setAdditionalPrompt(e.target.value)} />
            <OrangeButton icon={<ReloadOutlined />} style={{ width: 40, height: 40, flexShrink: 0 }} disabled={!isLoaded} onClick={retry} />
          </div>
          <Typography.Text className={styles.stepText}>Шаг 2. Работа с фоном</Typography.Text>
          {(!isLoadedForBackground || isImagesWithBackground) && (
            <>
              <div className={styles.imageWrapper}>
                {!isLoadedForBackground && <ThreeDot color='#FF7905' size='medium' text='' textColor='' />}
                {isErrorForBackground && isLoadedForBackground && (
                  <Result
                    status='error'
                    title='Ошибка при генерации изображения'
                    subTitle='Попробуйте еще раз'
                    extra={[
                      <OrangeButton onClick={retryForBackground} disabled={!isLoadedForBackground}>
                        Попробовать еще раз
                      </OrangeButton>
                    ]}
                  />
                )}
                {isLoadedForBackground && selectedImageWithBackground && !isErrorForBackground && <img src={`${IMAGES_URL}${selectedImageWithBackground.image}`} alt='product' className={styles.image} />}
              </div>
              {isImagesWithBackground && (
                <div className={styles.buttonsRow}>
                  {imagesWithBackground.map((image, index) => (
                    <VariantButton
                      text={`Вариант ${index + 1}`}
                      isActive={selectedImageWithBackground?._id === image._id}
                      onClick={() => setSelectedImageWithBackground(image)}
                      key={image._id}
                    />
                  ))}
                </div>
              )}
            </>
          )}
          <Input.TextArea autoSize={true} placeholder='Опишите на каком фоне должен быть товар, например “лежать на деревянной доске на сером фоне”' value={additionalPromptForBackground} onChange={(e) => setAdditionalPromptForBackground(e.target.value)} />
          <Button type='default' size='large' className={styles.generateBackgroundButton} onClick={retryForBackground} disabled={!isLoadedForBackground}><span className='aiButtonIcon'>􀜍</span> {isImagesWithBackground ? 'Перегенерировать' : 'Сгенерировать'}</Button>
          {isImagesWithBackground && (
            <OrangeButton size='large' style={{ marginTop: 10, width: '100%' }} onClick={onSave}>Сохранить</OrangeButton>
          )}
        </>
      )}
    </div>
  )
})